import { THEME_CONFIG } from "./config";
import cssVars from "./cssVars.json";
import { deriveDark, deriveLight, DerivedRoles } from "./derive";
import { flatten, hexToRgb } from "./oklch";
import palettes from "./palettes.json";
import scale from "./scale.json";
import type {
  BrandName,
  ColorRoles,
  Elevation,
  SemanticColors,
  Theme,
  ThemeMode,
  Tints,
  VibeName,
  VibeValues,
} from "./types";

export * from "./types";
export { scale };

const BRAND_NAMES = Object.keys(palettes.brands) as BrandName[];
type Recipe = typeof palettes.recipe.dark | typeof palettes.recipe.light;

/** The platform serves its own accent; that colour seeds a derived palette. */
function platformSeed(): string {
  if (typeof document === "undefined") return palettes.brands.violet.pure;
  const injected = (document as any)?.colours?.pink?.violetRed;
  if (typeof injected === "string" && /^#[0-9a-f]{6}$/i.test(injected)) return injected;
  return palettes.brands.violet.pure;
}

/**
 * A ?theme-brand= override, so the five domains can be compared without five hosts.
 * Development only — production resolves from the config and the hostname.
 */
function devBrandOverride(): BrandName | null {
  // process.env.NODE_ENV rather than import.meta.env: Vite defines both, and the unit
  // tests run through Babel's CommonJS transform, where import.meta is a parse error.
  if (typeof window === "undefined" || process.env.NODE_ENV === "production") return null;
  const asked = new URLSearchParams(window.location.search).get("theme-brand");
  return asked && BRAND_NAMES.includes(asked as BrandName) ? (asked as BrandName) : null;
}

/** Config wins, then hostname, then a palette derived from the platform's accent. */
export function resolveBrand(
  override?: BrandName
): { name: BrandName | "derived"; roles: Record<ThemeMode, DerivedRoles>; pure: string } {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const named = override ?? devBrandOverride() ?? THEME_CONFIG.byHostname[host] ?? THEME_CONFIG.brand;

  if (named && BRAND_NAMES.includes(named)) {
    const preset = palettes.brands[named];
    return { name: named, roles: { dark: preset.dark, light: preset.light }, pure: preset.pure };
  }

  const seed = THEME_CONFIG.seed ?? platformSeed();
  return { name: "derived", roles: { dark: deriveDark(seed), light: deriveLight(seed) }, pure: seed };
}

const rgb = (hex: string) => hexToRgb(hex).map((n) => Math.round(n * 255)).join(", ");
const alpha = (hex: string, a: number) => `rgba(${rgb(hex)}, ${a})`;

/**
 * Turn an accent percentage into a usable surface. Dark tints ride on transparency so
 * they stack over whatever surface they land on; light tints flatten onto the panel,
 * because a translucent accent over paper goes mid-tone and swallows its own label.
 */
function tintOver(accent: string, ratio: number, over: string, panel: string): string {
  if (ratio >= 1) return accent;
  return over === "panel" ? flatten(accent, ratio, panel) : alpha(accent, ratio);
}

function buildTints(roles: DerivedRoles, recipe: Recipe, semantic: SemanticColors): Tints {
  const { accent, panel, text, ink } = roles;
  return {
    box: tintOver(accent, recipe.tintBox, recipe.tintBoxOver, panel),
    boxRing: recipe.tintBoxRing ? `0 0 0 1px ${alpha(accent, recipe.tintBoxRing)}` : "none",
    boxLabelOpacity: recipe.tintBoxLabelOpacity,
    segment: tintOver(accent, recipe.tintSegment, recipe.tintSegmentOver, panel),
    segmentRing: recipe.tintSegmentRing ? `0 0 0 1px ${alpha(accent, recipe.tintSegmentRing)}` : "none",
    neutral: alpha(text, recipe.tintNeutral),
    chip: alpha(text, recipe.tintChip),
    checkbox: alpha(text, recipe.tintCheckbox),
    pill: tintOver(accent, recipe.tintPill, recipe.tintBoxOver, panel),
    // The own-message fill from the chat board: a 20% accent veil in the dark, and a
    // pale flattened tint on paper — a filled accent bubble would shout in a thread
    // where monetization is meant to be the only loud thing.
    chatOwn: tintOver(accent, recipe.chatOwn, recipe.tintBoxOver, panel),
    // 25a gives flames and rank their own tint so the eye finds them without a label.
    // They tint from the semantic colour, not the accent — a brand swap must not move
    // what a flame or a trophy looks like.
    flame: tintOver(semantic.flame, recipe.tintPill, recipe.tintBoxOver, panel),
    rank: tintOver(semantic.gold, recipe.tintPill, recipe.tintBoxOver, panel),
    // The accent gradient a single profile block may wear. Built from the same two
    // roles as everything else, so it follows a brand and a mode swap.
    blockAccent: `linear-gradient(120deg, ${flatten(accent, recipe.blockAccent, panel)}, ${panel} 78%)`,
    coverPlane: `linear-gradient(140deg, ${flatten(accent, recipe.coverPlane, roles.well)}, ${roles.well} 70%)`,
    grabHandle: alpha(text, recipe.grabHandle),
    // Sits on a photograph in either mode, so it does not follow the surface roles.
    scrim: recipe.scrim,
    applyBg: recipe.applyFill >= 1 ? accent : alpha(accent, recipe.applyFill),
    applyFg: recipe.applyUsesInk ? ink : accent,
    borderAccent: alpha(accent, recipe.borderAccent),
    hover: alpha(text, recipe.hover),
    pressed: alpha(text, recipe.pressed),
    overlay: recipe.overlay,
  };
}

function buildElevation(roles: DerivedRoles, recipe: Recipe): Elevation {
  const shadowRgb = roles.shadowRgb ?? "0, 0, 0";
  const textRgb = rgb(roles.text);
  const fill = (v: string) => v.replace(/\{shadowRgb\}/g, shadowRgb).replace(/\{textRgb\}/g, textRgb);
  const e = recipe.elevation;
  return {
    card: fill(e.card),
    panel: fill(e.panel),
    sheet: fill(e.sheet),
    post: fill(e.post),
    nav: fill(e.nav),
    chrome: fill(e.chrome),
  };
}

export const VIBE_NAMES = Object.keys(scale.vibes) as VibeName[];
export const DEFAULT_VIBE: VibeName = "default";

/** The stylesheet only ever needs one display family; the vibe says which. */
function resolveVibeValues(vibe: VibeName): VibeValues {
  const v = (scale.vibes[vibe] ?? scale.vibes[DEFAULT_VIBE]) as (typeof scale.vibes)[typeof DEFAULT_VIBE];
  return {
    fontDisplay: v.type.display === "serif" ? scale.type.familyDisplay : scale.type.family,
    headingScale: String(v.type.headingScale),
    headingTracking: v.type.headingTracking,
    panelPad: `${v.space.panelPad}px`,
    panelPadDesktop: `${v.space.panelPadDesktop}px`,
    stackGap: `${v.space.stackGap}px`,
    stackGapDesktop: `${v.space.stackGapDesktop}px`,
    motionFade: `${v.motion.fade}ms`,
    motionSheet: `${v.motion.sheet}ms`,
    motionTap: `${v.motion.tap}ms`,
  };
}

/** The URL of a face this vibe needs, if it needs one the others do not. */
export function vibeWebfont(vibe: VibeName): string | null {
  return (scale.vibes[vibe] as { webfont?: string })?.webfont ?? null;
}

export function resolveTheme(
  mode: ThemeMode,
  brandOverride?: BrandName,
  vibe: VibeName = DEFAULT_VIBE
): Theme {
  const brand = resolveBrand(brandOverride);
  const roles = brand.roles[mode];
  const recipe = palettes.recipe[mode];
  // Pills and avatars keep their base value in every vibe: a pill that stops being a
  // pill is a different control, not a softer one.
  const radius = { ...scale.radius, ...(scale.vibes[vibe] ?? scale.vibes[DEFAULT_VIBE]).radius };
  const vibeValues = resolveVibeValues(vibe);

  const color: ColorRoles = {
    ground: roles.ground,
    panel: roles.panel,
    card: roles.card,
    well: roles.well,
    text: roles.text,
    muted: alpha(roles.text, recipe.mutedAlpha),
    muted2: alpha(roles.text, recipe.muted2Alpha),
    accent: roles.accent,
    ink: roles.ink,
    brandPure: brand.pure,
  };

  return {
    brand: brand.name,
    mode,
    vibe,
    color,
    semantic: palettes.semantic[mode] as SemanticColors,
    tint: buildTints(roles, recipe, palettes.semantic[mode] as SemanticColors),
    elevation: buildElevation(roles, recipe),
    radius,
    vibeValues,
    size: scale.size,
    grid: scale.grid,
    type: scale.type,
    motion: scale.motion,
  };
}

/** CSS custom property names, kept in one place so Tailwind and the runtime agree. */
export const CSS_VARS = cssVars;

/** Flatten a resolved theme to the custom properties the stylesheet reads. */
export function themeToCssVars(theme: Theme): Record<string, string> {
  const out: Record<string, string> = {};
  const group = <T extends Record<string, string>>(vars: T, values: Record<string, string | number>) => {
    for (const key of Object.keys(vars)) out[vars[key]] = String(values[key]);
  };
  group(CSS_VARS.color, theme.color);
  group(CSS_VARS.semantic, theme.semantic);
  group(CSS_VARS.tint, theme.tint as unknown as Record<string, string | number>);
  group(CSS_VARS.elevation, theme.elevation);
  for (const [role, name] of Object.entries(CSS_VARS.radius)) {
    out[name] = `${(theme.radius as Record<string, number>)[role]}px`;
  }
  group(CSS_VARS.vibe, theme.vibeValues);
  return out;
}

export function applyTheme(mode: ThemeMode, vibe?: VibeName, brandOverride?: BrandName): Theme {
  const theme = resolveTheme(mode, brandOverride, vibe);
  const root = document.documentElement;
  const vars = themeToCssVars(theme);
  for (const name in vars) root.style.setProperty(name, vars[name]);
  root.dataset.themeMode = mode;
  root.dataset.themeBrand = theme.brand;
  root.dataset.themeVibe = theme.vibe;
  return theme;
}
